import React from 'react'

import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { useAuth } from '@fourthwall/auth'
import {
  ButtonClean,
  ConfirmModal,
  type TableHeaders,
  Toasts,
  useModalContext,
} from '@fourthwall/components'
import type { GetUsers, InviteUser } from '@fourthwall/services/dist/ShopUsersApiClient'
import { TeamMember } from '@modules/Dashboard/pages/Settings/pages/Team/components/TeamMember/TeamMember'
import { useUsersQuery } from '@queries/useUsersQuery'
import type { UseQueryOptions } from '@tanstack/react-query'

import {
  AddSubscriptionModal,
  SubscriptionLimitModal,
  useSubscription,
} from '../../../../../modules/Subscriptions'
import type { GetUsersResponse, TeamMemberItem } from '../Team.types'
import { RoleModal } from '../components/RoleModal/RoleModal'
import type { FormValues as RoleModalFormValues } from '../components/RoleModal/RoleModal.types'
import type { TeamMemberDropdownProps } from '../components/TeamMemberDropdown/TeamMemberDropdown'
import { TeamMemberDropdown } from '../components/TeamMemberDropdown/TeamMemberDropdown'
import { useCancelInvitationsMutation } from '../queries/useCancelInvitationsMutation'
import { useResendInvitationMutation } from '../queries/useResendInvitationMutation'
import { useUserInviteMutation } from '../queries/useUserInviteMutation'
import { useUserRemoveMutation } from '../queries/useUserRemoveMutation'
import { useUserUpdateMutation } from '../queries/useUserUpdateMutation'
import { sortTeamUsers } from '../utils/sortTeamUsers'
import { useTeamMembersRoles } from './useTeamMembersRoles'

export type UseTeamTabProps = {
  options?: UseQueryOptions<GetUsersResponse>
}

export const useTeamTab = ({ options }: UseTeamTabProps) => {
  const { logout } = useAuth()
  const { hasPermission } = useDashboardPermissions()
  const { open } = useModalContext()
  const { usersQuery, invalidateUsersQuery } = useUsersQuery([], options)
  const { activeSubscription, teamMembers, canUpgradeSubscription, invalidateSubscriptionData } =
    useSubscription()
  const { userInviteMutation } = useUserInviteMutation()
  const { resendInvitationMutation } = useResendInvitationMutation()
  const { cancelInvitationsMutation } = useCancelInvitationsMutation()
  const { userRemoveMutation } = useUserRemoveMutation()
  const { userUpdateMutation } = useUserUpdateMutation()
  const {
    availableRoles,
    topRoleId,
    getRoleName,
    selectableRoles,
    canUserManageTopRoleTeamMembers,
  } = useTeamMembersRoles()
  const { user } = useAuth()

  const areManyTopRoleTeamMembers =
    ((topRoleId &&
      usersQuery.data?.users?.filter((user) => user.group === topRoleId && user.userId)?.length) ||
      0) > 1

  const openRoleModal = ({
    ...props
  }: {
    type?: 'invitation' | 'edit'
    initialValues?: RoleModalFormValues
    name?: string
    onSubmit: ({ email, role }: RoleModalFormValues) => void
  }) => {
    open(RoleModal, {
      ...props,
      selectableRoles,
    })
  }

  const handleUserInvite = ({ email, role }: RoleModalFormValues) => {
    // TODO remove as InviteUserPayload when InviteUserPayload is ready to receive role: string
    const payload = { email, role } as InviteUser['Payload']
    userInviteMutation.mutate(payload, {
      onSuccess: (response) => {
        Toasts.notify(`${response.invitedUserEmail} will receive an invitation email shortly.`, {
          type: 'info',
        })
        invalidateUsersQuery()
        availableRoles.invalidate()
        invalidateSubscriptionData()
      },
    })
  }

  const handleUpgradeSubscription = ({ onSuccess }: { onSuccess?: () => void } = {}) => {
    open(AddSubscriptionModal, {
      title: 'Upgrade your plan to add more than 3 team members',
      source: 'settings_team',
      onSuccess,
    })
  }

  const handleInviteClick = () => {
    if (activeSubscription && !teamMembers.canCreate && !canUpgradeSubscription) {
      open(SubscriptionLimitModal, {
        title: 'Team members limit reached',
        content: (
          <>
            You've invited {activeSubscription?.members.usage} out of{' '}
            {activeSubscription?.members.limit} team members allowed on your current plan. To invite
            someone new, please remove an existing member{' '}
            <ButtonClean to="mailto:support@fourthwall.com" label="contact our support team" />.
          </>
        ),
      })

      return
    }

    if (!teamMembers.canCreate && canUpgradeSubscription) {
      handleUpgradeSubscription({
        onSuccess: () => {
          openRoleModal({
            onSubmit: handleUserInvite,
          })
        },
      })

      return
    }

    openRoleModal({
      onSubmit: handleUserInvite,
    })
  }

  const handleChangeRole = ({ email, role }: RoleModalFormValues) => {
    // TODO Remove unnecessary parameters when changing role endpoint is ready
    const user = usersQuery.data?.users.find((user) => user.email === email)

    if (user) {
      const payload = { ...user, group: role }

      userUpdateMutation.mutate([payload], {
        onSuccess: () => {
          Toasts.notify(
            `The role of ${user.fullName || email} has been successfully changed to ${getRoleName(
              role,
            )}.`,
            {
              type: 'info',
            },
          )
          invalidateUsersQuery()
          availableRoles.invalidate()
          invalidateSubscriptionData()
        },
      })
    }
  }

  const handleChangeRoleClick = (role: string, email: string, fullName?: string) => {
    openRoleModal({
      type: 'edit',
      onSubmit: handleChangeRole,
      initialValues: { email, role },
      name: fullName,
    })
  }

  const handleResendInvitation = (invitationId: string) => {
    resendInvitationMutation.mutate(
      { id: invitationId },
      {
        onSuccess: () => {
          Toasts.notify('The invitation has been resent.', { type: 'info' })
          invalidateUsersQuery()
          invalidateSubscriptionData()
        },
      },
    )
  }

  const handleInvitationCancel = (invitedUserEmail: string) => {
    cancelInvitationsMutation.mutate(
      { userEmail: invitedUserEmail },
      {
        onSuccess: () => {
          Toasts.notify('The invitation has been canceled.', { type: 'info' })
          invalidateUsersQuery()
          invalidateSubscriptionData()
        },
      },
    )
  }

  const handleUserRemove = (email: string, fullName?: string) => {
    const isYou = user?.email === email

    if (isYou) {
      open(ConfirmModal, {
        title: 'Are you sure you want to leave this team?',
        text: 'You will no longer have access to the admin tools for this site.',
        confirmLabel: 'Yes, leave',
        confirmAppearance: 'destructive',
        onConfirm: () => {
          userRemoveMutation.mutate(
            { email },
            {
              onSuccess: () => {
                Toasts.notify('You have left the team.', {
                  type: 'info',
                })

                logout()
              },
            },
          )
        },
      })
    }

    open(ConfirmModal, {
      title: `Are you sure you want to remove ${fullName || 'this user'} as a team member?`,
      text: `${fullName || 'This user'} will no longer have access to the admin tools for this site.`,
      confirmLabel: 'Yes, remove team member',
      confirmAppearance: 'destructive',
      onConfirm: () => {
        userRemoveMutation.mutate(
          { email },
          {
            onSuccess: () => {
              Toasts.notify('The user has been removed.', {
                type: 'info',
              })

              invalidateUsersQuery()
              invalidateSubscriptionData()
            },
          },
        )
      },
    })
  }

  const canUserResendInvitation = (teamMember: GetUsers['Response']['users'][number]): boolean => {
    const hasPermissions = hasPermission('settings.team.invite')
    const isTeamMemberInvited =
      teamMember.invitationStatus === 'INVITED' && !!teamMember.invitationId
    const canManageTeamMember = teamMember.group !== topRoleId || canUserManageTopRoleTeamMembers

    return hasPermissions && isTeamMemberInvited && canManageTeamMember
  }

  const canUserCancelInvitation = (teamMember: GetUsers['Response']['users'][number]): boolean => {
    const hasPermissions = hasPermission('settings.team.invite')
    const hasRequiredProps = !!teamMember.email
    const isTeamMemberInvited =
      teamMember.invitationStatus === 'INVITED' && !!teamMember.invitationId
    const canManageTeamMember = teamMember.group !== topRoleId || canUserManageTopRoleTeamMembers

    return hasPermissions && hasRequiredProps && isTeamMemberInvited && canManageTeamMember
  }

  const canUserChangeRole = (teamMember: GetUsers['Response']['users'][number]): boolean => {
    const hasPermissions = hasPermission('settings.team.changeRole')
    const hasRequiredProps = !!teamMember.email
    const canManageTeamMember = teamMember.group !== topRoleId || canUserManageTopRoleTeamMembers
    const isOnlyTopRoleTeamMember =
      teamMember.group === topRoleId && !!teamMember.userId && !areManyTopRoleTeamMembers
    const isYou = teamMember.email === user?.email

    return (
      hasPermissions &&
      hasRequiredProps &&
      canManageTeamMember &&
      !isOnlyTopRoleTeamMember &&
      !isYou
    )
  }

  const canUserRemoveTeamMember = (teamMember: GetUsers['Response']['users'][number]): boolean => {
    const hasPermissions = hasPermission('settings.team.remove')
    const hasRequiredProps = !!teamMember.email && !!teamMember.userId
    const canManageTeamMember = teamMember.group !== topRoleId || canUserManageTopRoleTeamMembers
    const isOnlyTopRoleTeamMember =
      teamMember.group === topRoleId && !!teamMember.userId && !areManyTopRoleTeamMembers
    const isYou = teamMember.email === user?.email

    return (
      (hasPermissions || isYou) &&
      hasRequiredProps &&
      canManageTeamMember &&
      !isOnlyTopRoleTeamMember
    )
  }

  const data: TeamMemberItem[] = (() => {
    const partnersData = usersQuery.data?.agencies ?? []
    const sortedTeamMembers = sortTeamUsers(
      user?.email,
      usersQuery.data?.users,
      availableRoles.roles,
    )

    return [
      ...sortedTeamMembers.map((teamMember) => ({
        name: teamMember.fullName || '',
        isYou: teamMember.email === user?.email,
        email: teamMember.email,
        role: teamMember.group,
        roleName: getRoleName(teamMember.group),
        invitationId: teamMember.invitationId,
        invitationStatus: teamMember.invitationStatus,
        permissions: {
          resendInvitation: canUserResendInvitation(teamMember),
          cancelInvitation: canUserCancelInvitation(teamMember),
          changeRole: canUserChangeRole(teamMember),
          remove: canUserRemoveTeamMember(teamMember),
        },
      })),
      ...partnersData.map((partner) => ({
        isPartner: true,
        name: partner.name,
        logoUri: partner.logoUri,
        isYou: false,
        roleName: topRoleId ? getRoleName(topRoleId) : '',
      })),
    ]
  })()

  const isLoading = usersQuery.isLoading || availableRoles.loading
  const emptyMessage = 'No users found'
  const dropdownProps: Omit<TeamMemberDropdownProps, 'teamMember'> = {
    onResendInvitation: handleResendInvitation,
    onInvitationCancel: handleInvitationCancel,
    onUserRemove: handleUserRemove,
    onChangeRole: handleChangeRoleClick,
  }

  const tableHeaders: TableHeaders<TeamMemberItem> = [
    {
      id: 'name',
      label: 'Name',
      render: (teamMember) => <TeamMember teamMember={teamMember} />,
      width: 2,
    },
    {
      id: 'role',
      label: 'Role',
      render: ({ roleName }) => roleName,
    },
    {
      id: 'select',
      label: ' ',
      render: (teamMember) => <TeamMemberDropdown teamMember={teamMember} {...dropdownProps} />,
      width: 0,
      minWidth: 40,
      alignment: 'right',
    },
  ]

  return {
    teamTab: {
      listProps: {
        data,
        isLoading,
        emptyComponent: emptyMessage,
      },
      dropdownProps,
      tableProps: {
        headers: tableHeaders,
        data,
        isLoading: usersQuery.isLoading || availableRoles.loading,
        emptyMessage,
      },
      buttonProps:
        hasPermission('settings.team.invite') && availableRoles
          ? {
              label: 'Invite new member',
              onClick: handleInviteClick,
            }
          : undefined,
    },
  }
}
